import { exportErrorFragment } from "@saleor/fragments/errors";
import { exportFileFragment } from "@saleor/fragments/products";
import makeMutation from "@saleor/hooks/makeMutation";
import makeQuery from "@saleor/hooks/makeQuery";
import gql from "graphql-tag";

import {
  GenerateGroupLogs,
  GenerateGroupLogsVariables
} from "./types/GenerateGroupLogs";
import {
  MfaVerifyCode,
  MfaVerifyCodeVariables
} from "./types/MfaEmailVerifyCode";
import {
  MfaPhoneSendCode,
  MfaPhoneSendCodeVariables
} from "./types/MfaPhoneSendCode";
import {
  MfaPhoneVerifyCode,
  MfaPhoneVerifyCodeVariables
} from "./types/MfaPhoneVerifyCode";
import {
  MfaSendCodeAttempt,
  MfaSendCodeAttemptVariables
} from "./types/MfaSendCodeAttempt";
import {
  MfaSendEmailCode,
  MfaSendEmailCodeVariables
} from "./types/mfaSendEmailCode";
import {
  MfaPhoneSendCodeAttempt,
  MfaPhoneSendCodeAttemptVariables
} from "./types/MfaSendPhoneCodeAttempt";
import {
  MfaVerifyCodeAttempt,
  MfaVerifyCodeAttemptVariables
} from "./types/MfaVerifyCodeAttempt";
import {
  MfaPhoneVerifyCodeAttempt,
  MfaPhoneVerifyCodeAttemptVariables
} from "./types/MfaVerifyPhoneCodeAttempt";

export const mfaVerifyCode = gql`
  mutation MfaVerifyCode($email: String!, $code: String!) {
    mfaVerifyCode(email: $email, code: $code) {
      success
      message
      token
      refreshToken
      csrfToken
      user {
        id
        email
        firstName
        lastName
      }
      data {
        lockoutPeriod
        codeValidity
        mfaAttempts
        maxAttempts
        resendCooldown
        lockedAt
      }
      accountErrors {
        field
        message
        code
      }
    }
  }
`;

export const useMfaEmailVerifyCode = makeMutation<
  MfaVerifyCode,
  MfaVerifyCodeVariables
>(mfaVerifyCode);

export const mfaPhoneVerifyCode = gql`
  mutation MfaPhoneVerifyCode($email: String!, $code: String!) {
    mfaPhoneVerifyCode(email: $email, code: $code) {
      success
      message
      token
      refreshToken
      csrfToken
      user {
        id
        email
        firstName
        lastName
        defaultMfaMethod
      }
      data {
        lockoutPeriod
        codeValidity
        mfaAttempts
        maxAttempts
        resendCooldown
        lockedAt
      }
      accountErrors {
        field
        message
        code
      }
    }
  }
`;

export const useMfaPhoneVerifyCode = makeMutation<
  MfaPhoneVerifyCode,
  MfaPhoneVerifyCodeVariables
>(mfaPhoneVerifyCode);

export const mfaSendEmailCode = gql`
  mutation MfaSendEmailCode($email: String!) {
    mfaSendCode(email: $email) {
      success
      message
      data {
        id
        lockoutPeriod
        codeValidity
        createdAt
        mfaAttempts
        maxAttempts
        resendCooldown
        lockedAt
      }
    }
  }
`;

export const useMfaSendEmailCode = makeMutation<
  MfaSendEmailCode,
  MfaSendEmailCodeVariables
>(mfaSendEmailCode);

export const mfaPhoneSendCode = gql`
  mutation MfaPhoneSendCode($email: String!) {
    mfaPhoneSendCode(email: $email) {
      success
      message
      data {
        id
        lockoutPeriod
        codeValidity
        createdAt
        mfaAttempts
        maxAttempts
        resendCooldown
        lockedAt
      }
    }
  }
`;

export const useMfaPhoneSendCode = makeMutation<
  MfaPhoneSendCode,
  MfaPhoneSendCodeVariables
>(mfaPhoneSendCode);

export const mfaSendCodeAttempt = gql`
  query MfaSendCodeAttempt($identifier: String!) {
    mfaSendCodeAttempt(identifier: $identifier) {
      id
      codeValidity
      resendCooldown
      mfaAttempts
      lockoutPeriod
      maxAttempts
      createdAt
      messageCode
      statusCode
      lockedAt
    }
  }
`;

export const useMfaSendCodeAttempt = makeQuery<
  MfaSendCodeAttempt,
  MfaSendCodeAttemptVariables
>(mfaSendCodeAttempt);

export const mfaVerifyCodeAttempt = gql`
  query MfaVerifyCodeAttempt($identifier: String!) {
    mfaVerifyCodeAttempt(identifier: $identifier) {
      id
      codeValidity
      resendCooldown
      mfaAttempts
      lockoutPeriod
      maxAttempts
      createdAt
      messageCode
      statusCode
      lockedAt
    }
  }
`;

export const useMfaVerifyCodeAttempt = makeQuery<
  MfaVerifyCodeAttempt,
  MfaVerifyCodeAttemptVariables
>(mfaVerifyCodeAttempt);

export const mfaPhoneSendCodeAttempt = gql`
  query MfaPhoneSendCodeAttempt($identifier: String!) {
    mfaPhoneSendCodeAttempt(identifier: $identifier) {
      id
      codeValidity
      resendCooldown
      mfaAttempts
      lockoutPeriod
      maxAttempts
      createdAt
      messageCode
      statusCode
      lockedAt
    }
  }
`;

export const useMfaPhoneSendCodeAttempt = makeQuery<
  MfaPhoneSendCodeAttempt,
  MfaPhoneSendCodeAttemptVariables
>(mfaPhoneSendCodeAttempt);

export const mfaPhoneVerifyCodeAttempt = gql`
  query MfaPhoneVerifyCodeAttempt($identifier: String!) {
    mfaPhoneVerifyCodeAttempt(identifier: $identifier) {
      id
      codeValidity
      resendCooldown
      mfaAttempts
      lockoutPeriod
      maxAttempts
      createdAt
      messageCode
      statusCode
      lockedAt
    }
  }
`;

export const useMfaPhoneVerifyCodeAttempt = makeQuery<
  MfaPhoneVerifyCodeAttempt,
  MfaPhoneVerifyCodeAttemptVariables
>(mfaPhoneVerifyCodeAttempt);

export const generateGroupLogs = gql`
  ${exportFileFragment}
  ${exportErrorFragment}
  mutation GenerateGroupLogs($input: ExportGroupLogsInput!) {
    exportGroupLogs(input: $input) {
      exportFile {
        ...ExportFileFragment
      }
      errors: exportErrors {
        ...ExportErrorFragment
      }
    }
  }
`;

export const useGenerateGroupLogs = makeMutation<
  GenerateGroupLogs,
  GenerateGroupLogsVariables
>(generateGroupLogs);
